import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import ProgressBar from '../components/common/ProgressBar'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatPercent,
  formatMonth,
  getCountryName
} from '../utils/formatters'

export default function FinancialTargetsOverview() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const targetMonth = selectedMonth || months[months.length - 1]

    // Filter financial targets
    let targets = data.financialTargets.filter(t => t.month === targetMonth)

    if (selectedCountry) {
      targets = targets.filter(t => t.country === selectedCountry)
    }

    // Aggregate targets
    const totals = targets.reduce((acc, t) => {
      acc.arrTarget += t.arr_target || 0
      acc.arrActual += t.arr_actual || 0
      acc.netNewTarget += t.net_new_arr_target || 0
      acc.netNewActual += t.net_new_arr_actual || 0
      acc.onboardingTarget += t.onboarding_revenue_target || 0
      acc.onboardingActual += t.onboarding_revenue_actual || 0
      return acc
    }, {
      arrTarget: 0,
      arrActual: 0,
      netNewTarget: 0,
      netNewActual: 0,
      onboardingTarget: 0,
      onboardingActual: 0
    })

    // Get movements for the month
    let movements = data.arrMovements.filter(m => m.month === targetMonth)
    if (selectedCountry) {
      movements = movements.filter(m => m.country === selectedCountry)
    }

    // Aggregate movements by type
    const movementTotals = movements.reduce((acc, m) => {
      const type = m.movement_type
      if (!acc[type]) acc[type] = 0
      acc[type] += Math.abs(m.arr_change)
      return acc
    }, {})

    // Movement table data
    const movementTableData = movements
      .sort((a, b) => b.arr_change - a.arr_change)
      .map(m => ({
        ...m,
        id: m.movement_id
      }))

    return {
      targetMonth,
      totals,
      movementTotals,
      movementTableData
    }
  }, [data, computed, selectedCountry, selectedMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const { targetMonth, totals, movementTotals, movementTableData } = filteredData

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <FilterDropdown
            label="Country"
            value={selectedCountry}
            options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
            onChange={setSelectedCountry}
            placeholder="All Countries"
          />
          <FilterDropdown
            label="Month"
            value={selectedMonth}
            options={computed.months.map(m => ({ value: m, label: formatMonth(m) }))}
            onChange={setSelectedMonth}
            placeholder="Latest Month"
          />
        </div>
      </div>

      {/* KPI Cards with Progress */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">ARR</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totals.arrActual, { compact: true })}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <CurrencyDollarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <ProgressBar
            value={totals.arrActual}
            max={totals.arrTarget}
            colorScheme="auto"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Target: {formatCurrency(totals.arrTarget, { compact: true })}
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Net New ARR</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totals.netNewActual, { compact: true })}
              </p>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <ProgressBar
            value={totals.netNewActual}
            max={totals.netNewTarget}
            colorScheme="auto"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Target: {formatCurrency(totals.netNewTarget, { compact: true })}
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">New Business</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(movementTotals['New Business'] || 0, { compact: true })}
              </p>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Churn</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(movementTotals['Churn'] || 0, { compact: true })}
              </p>
            </div>
            <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Movement KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard
          title="Expansion Upsell"
          value={formatCurrency(movementTotals['Expansion Upsell'] || 0, { compact: true })}
          icon={ArrowTrendingUpIcon}
          colorScheme="success"
        />
        <KPICard
          title="Expansion Resell"
          value={formatCurrency(movementTotals['Expansion Resell'] || 0, { compact: true })}
          icon={ArrowTrendingUpIcon}
          colorScheme="success"
        />
        <KPICard
          title="Contraction Downsell"
          value={formatCurrency(movementTotals['Contraction Downsell'] || 0, { compact: true })}
          icon={ArrowTrendingDownIcon}
          colorScheme="warning"
        />
        <KPICard
          title="Contraction Resell"
          value={formatCurrency(movementTotals['Contraction Resell'] || 0, { compact: true })}
          icon={ArrowTrendingDownIcon}
          colorScheme="warning"
        />
      </div>

      {/* Movement Table */}
      <ChartCard title="Alias Overview" subtitle={`Movements for ${formatMonth(targetMonth)}`} height="h-auto">
        <DataTable
          columns={[
            { key: 'month', header: 'Month', render: v => formatMonth(v) },
            { key: 'customer_id', header: 'Alias', render: (v, row) => (
              <span className="text-brand-600 dark:text-brand-400 font-medium">
                {v}
              </span>
            )},
            { key: 'organization_name', header: 'Organization' },
            { key: 'country', header: 'Country', render: v => getCountryName(v) },
            { key: 'arr_change', header: 'ARR Change', render: v => (
              <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {formatCurrency(v)}
              </span>
            )},
            { key: 'movement_type', header: 'Movement', render: v => (
              <span className={`badge ${
                v === 'New Business' || v.includes('Expansion') ? 'badge-success' :
                v === 'Churn' ? 'badge-danger' : 'badge-warning'
              }`}>
                {v}
              </span>
            )},
            { key: 'account_manager', header: 'Account Manager' },
            { key: 'revenue_stream', header: 'Revenue Stream' },
            { key: 'subscription_type', header: 'Subscription' }
          ]}
          data={movementTableData}
          pageSize={10}
          searchable={true}
          searchPlaceholder="Search movements..."
        />
      </ChartCard>
    </div>
  )
}
